<template>
  <div v-if="!loading">
    <VeeForm @submit="handleSubmit()" class="form" v-if="!isDone">
      <div v-for="(question, index) in questionsData" :key="index">
        <Radio v-bind:props="question" v-bind="{ question, validate }"
          @validation-effect="(e) => handleValidationEffect(e, index)" />
      </div>

      <MDBRow>
        <MDBCol>
          <MDBBtn type="submit" color="danger" :disabled="submitting"
            class="d-flex justify-content-center align-items-center"><span>{{ $t("global.buttons.send") }}</span>
            <Loader :show="submitting" :small="true" spinner="spinner.svg" :tiny="true" />
          </MDBBtn>
        </MDBCol>
      </MDBRow>
    </VeeForm>
    <Summary :training="trainingData" @store-points="handleSubmit()" @download-certificate="downloadCertificate()"
      v-else />
  </div>
  <Loader :show="loading" v-else />
</template>

<script setup>
import { Form as VeeForm } from "vee-validate";
import { useRoute } from "vue-router";
import Radio from "@/components/Trainings/Form/Inputs/Radio.vue";
import Summary from "@/components/Trainings/Form/Result/Summary.vue";
import Loader from "@/components/General/Loader/index.vue";
import { MDBCol, MDBRow, MDBBtn } from "mdb-vue-ui-kit";
import { ref, computed, onMounted } from "vue";
import { useStore } from "vuex";
import { PDFDocument, rgb } from "pdf-lib";
import fontkit from "@pdf-lib/fontkit";
import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";
import { useI18n } from "vue-i18n";
const { t } = useI18n() // Import the translation function from i18n

const store = useStore();
const route = useRoute();
const profile = computed(() => store.getters.getProfile);
let showSuccessModal = ref(false);
const loading = ref(true);
const submitting = ref(false);
const certificates = computed(() => store.getters.getCertificateTexts);
const training = computed(() => store.getters.getTraining);
const storedLocale = computed(() => store.getters.getCurrentLocale);

const downloadCertificate = async () => {
  if (!profile.value || !profile.value.name || !profile.value.forename) {
    toast.clearAll();

    toast(t("toast.fillProfile"), {
      type: "error",
      theme: "colored",
      transition: toast.TRANSITIONS.SLIDE,
      position: toast.POSITION.TOP_RIGHT,
    });
    return false;
  }
  let url = `${window.location.origin}/files/blank-verkaufsstarke-certificate.pdf`
  const certificatePaths = {
    'da-DK': 'files/certificates/da-DK/blanko-bs-training-center-certificate.pdf',
    'de-AT': 'files/certificates/de-AT/blanko-bs-training-center-certificate.pdf',
    'de-DE': 'files/certificates/de-DE/blanko-bs-training-center-certificate.pdf',
    'de-CH': 'files/certificates/de-DE/blanko-bs-training-center-certificate.pdf',
    'fr-CH': 'files/certificates/de-DE/blanko-bs-training-center-certificate.pdf',
    'it-CH': 'files/certificates/de-DE/blanko-bs-training-center-certificate.pdf'
  }

  if (certificatePaths[storedLocale.value]) {
    url = `${window.location.origin}/${certificatePaths[storedLocale.value]}`;
  }

  const existingPdfBytes = await fetch(url).then((res) => res.arrayBuffer());

  const pdfDoc = await PDFDocument.load(existingPdfBytes);

  const decimalBookUrl = `${window.location.origin}/fonts/Decimal/Decimal-Book.otf`;
  const decimalBook = await fetch(decimalBookUrl).then((res) =>
    res.arrayBuffer()
  );
  pdfDoc.registerFontkit(fontkit);
  const decimalBookFont = await pdfDoc.embedFont(decimalBook);
  const decimalMediumUrl = `${window.location.origin}/fonts/Decimal/Decimal-Medium.otf`;
  const decimalMedium = await fetch(decimalMediumUrl).then((res) =>
    res.arrayBuffer()
  );
  pdfDoc.registerFontkit(fontkit);
  const decimalMediumFont = await pdfDoc.embedFont(decimalMedium);

  const pages = pdfDoc.getPages();
  const firstPage = pages[0];
  const { height } = firstPage.getSize();
  const drawTextWithBg = ({
    text,
    x,
    y,
    size,
    font,
    textColor = rgb(0, 0, 0),
  }) => {
    if (!text) return;

    firstPage.drawText(text, {
      x,
      y,
      size,
      font,
      color: textColor,
    });
  };

  firstPage.drawText(t("trainings.certificate.header"), {
    x: 40,
    y: height / 2 + 180,
    size: 50,
    font: decimalBookFont,
    color: rgb(0, 0, 0),
  });

  drawTextWithBg({
    text: `${profile.value.forename} ${profile.value.name}`,
    x: 40,
    y: height / 2 + 125,
    size: 20,
    font: decimalBookFont,
  });

  drawTextWithBg({
    text: t("trainings.certificate.atCourse"),
    x: 40,
    y: height / 2 + 85,
    size: 15,
    font: decimalMediumFont,
  });

  if (training.value.title[storedLocale.value].trim().split(/\s+/).length > 5) {
    drawTextWithBg({
      text: `${training.value.title[storedLocale.value].split(" ").slice(0, 5).join(" ").trim()}`,
      x: 40,
      y: height / 2 + 65,
      size: 15,
      font: decimalBookFont,
    });
    drawTextWithBg({
      text: `${training.value.title[storedLocale.value]
        .split(" ")
        .slice(5, training.value.title[storedLocale.value].trim().split(/\s+/).length + 1)
        .join(" ")
        .trim()}`,
      x: 40,
      y: height / 2 + 50,
      size: 15,
      font: decimalBookFont,
    });
  } else {
    drawTextWithBg({
      text: `${training.value.title[storedLocale.value]}`,
      x: 40,
      y: height / 2 + 55,
      size: training.value.title[storedLocale.value].length > 40 ? 15 : 20,
      font: decimalBookFont,
    });
  }

  drawTextWithBg({
    text: t("trainings.certificate.contains"),
    x: 40,
    y: height / 2 - 35,
    size: 14,
    font: decimalBookFont,
  });

  const data = new Date(endQuizDate.value);

  drawTextWithBg({
    text: `${t("trainings.certificate.country")}, ${data.getDate()}.${data.getMonth() + 1
      }.${data.getFullYear()}`,
    x: 40,
    y: height / 2 - 180,
    size: 11,
    font: decimalMediumFont,
  });

  if (certificates.value.length > 0) {
    for (let i = 0; i < certificates.value.length; i++) {
      drawTextWithBg({
        text: certificates.value[i].text[storedLocale.value],
        x: certificates.value[i].positionX,
        y: certificates.value[i].positionY,
        size: certificates.value[i].size,
        font: decimalMediumFont,
      });
    }
  }

  const pdfBytes = await pdfDoc.save();

  const blob = new Blob([pdfBytes], { type: "application/pdf" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = setCertificateName(training.value.title[storedLocale.value]);
  link.click();
  URL.revokeObjectURL(link.href);
};

const setCertificateName = (str) => {
  return (
    "Certificate_" +
    String(str)
      .normalize("NFKD") // split accented characters into their base characters and diacritical marks
      .replace(/[\u0300-\u036f]/g, "") // remove all the accents, which happen to be all in the \u03xx UNICODE block.
      .trim() // trim leading or trailing whitespace
      .toLowerCase() // convert to lowercase
      .replace(/[^a-z0-9 -]/g, "") // remove non-alphanumeric characters
      .replace(/\s+/g, "-") // replace spaces with hyphens
      .replace(/-+/g, "-")
  ); // remove consecutive hyphens
};

const trainingData = computed(() => store.getters.getTraining);
const questionsData = computed(() => store.getters.getQuestions);
const user = computed(() => store.getters.getMe);

const isDone = computed(() => store.getters.getTrainingResult);
const endQuizDate = computed(() => store.getters.getEndQuizDate);

let correctAnswers = ref([]);

const validate = ref(0);

const handleSubmit = async () => {
  submitting.value = true
  validate.value = validate.value + 1;

  if (correctAnswers.value.length == Object.keys(questionsData.value).length) {
    const data = {
      user_id: user.value.id,
      training_id: trainingData.value.id,
      attempt: validate.value,
      calculated_points: trainingData.value.points[storedLocale.value],
    };

    const response = await store.dispatch("storeResult", data);

    if (response) {
      showSuccessModal.value = true;
      await store.dispatch("loadTrainingById", route.params.id);
      await store.dispatch("me");
    }
  }

  submitting.value = false
};

const handleValidationEffect = (correctAnswer, key) => {
  if (!correctAnswer && correctAnswers.value.includes(key)) {
    correctAnswers.value = correctAnswers.value.filter((item) => item !== key);
  } else if (correctAnswer && !correctAnswers.value.includes(key)) {
    correctAnswers.value.push(key);
  }
};

onMounted(async () => {
  loading.value = true;
  await store.dispatch("loadTrainingById", route.params.id);
  loading.value = false;
});

</script>

<style scoped lang="scss">
@import "@/styles/variables";
</style>
